/**
 * Client-side bucketing for AI difficulty rows.
 *
 * Backend emits one row per raw `aidiff` (e.g. 47, 51, 32…). With real
 * traffic that's many rows. These helpers let the dashboard re-aggregate
 * them into ranges (5 / 10 / 20). Used by the global AI analytics page
 * and the per-player analytics tab.
 *
 * Sum semantics:
 *   - breakdown rows: matches/wins/losses/ties summed, winRate recomputed
 *   - distribution rows: count summed, percentage recomputed against total
 *   - run-rate rows: avgRR averaged *weighted by matches* to avoid a
 *     1-match outlier dominating a 100-match bucket
 *
 * Non-numeric `value` (e.g. backend labels like "Easy"/"Medium"/"Hard")
 * are passed through ungrouped so they remain visible at the bottom.
 */
import type { AiBreakdownItem, AiDistributionItem } from '@/types';

export type DifficultyBucketSize = 'individual' | 5 | 10 | 20;

const numericValue = (v: unknown): number | null => {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v === 'string') {
    const n = parseInt(v, 10);
    return Number.isFinite(n) ? n : null;
  }
  return null;
};

const bucketKey = (value: number, size: number): string => {
  const start = Math.floor(value / size) * size;
  return `${start}-${start + size - 1}`;
};

export function bucketBreakdownRows(
  rows: AiBreakdownItem[],
  size: DifficultyBucketSize
): AiBreakdownItem[] {
  if (size === 'individual') return rows;
  const groups = new Map<string, AiBreakdownItem & { sortKey: number }>();
  for (const r of rows) {
    const v = numericValue(r.value);
    if (v == null) {
      const existing = groups.get(r.label);
      if (existing) {
        existing.matches += r.matches;
        existing.wins += r.wins;
        existing.losses += r.losses;
        existing.ties += r.ties;
      } else {
        groups.set(r.label, { ...r, sortKey: Number.MAX_SAFE_INTEGER });
      }
      continue;
    }
    const key = bucketKey(v, size);
    const sortKey = Math.floor(v / size) * size;
    const existing = groups.get(key);
    if (existing) {
      existing.matches += r.matches;
      existing.wins += r.wins;
      existing.losses += r.losses;
      existing.ties += r.ties;
    } else {
      groups.set(key, {
        label: key,
        value: sortKey,
        matches: r.matches,
        wins: r.wins,
        losses: r.losses,
        ties: r.ties,
        winRate: 0,
        sortKey,
      });
    }
  }
  return Array.from(groups.values())
    .map((g) => ({
      ...g,
      winRate: g.matches > 0 ? Math.round((g.wins / g.matches) * 1000) / 10 : 0,
    }))
    .sort((a, b) => a.sortKey - b.sortKey)
    .map(({ sortKey: _drop, ...rest }) => rest);
}

export function bucketDistributionRows(
  rows: AiDistributionItem[],
  size: DifficultyBucketSize,
  totalForPct?: number
): AiDistributionItem[] {
  if (size === 'individual') return rows;
  const total = totalForPct ?? rows.reduce((s, r) => s + r.count, 0);
  const groups = new Map<string, AiDistributionItem & { sortKey: number }>();
  for (const r of rows) {
    const v = numericValue(r.value);
    if (v == null) {
      const existing = groups.get(r.label);
      if (existing) existing.count += r.count;
      else groups.set(r.label, { ...r, sortKey: Number.MAX_SAFE_INTEGER });
      continue;
    }
    const key = bucketKey(v, size);
    const sortKey = Math.floor(v / size) * size;
    const existing = groups.get(key);
    if (existing) existing.count += r.count;
    else
      groups.set(key, {
        label: key,
        value: sortKey,
        count: r.count,
        percentage: 0,
        sortKey,
      });
  }
  return Array.from(groups.values())
    .map((g) => ({
      ...g,
      percentage: total > 0 ? Math.round((g.count / total) * 1000) / 10 : 0,
    }))
    .sort((a, b) => a.sortKey - b.sortKey)
    .map(({ sortKey: _drop, ...rest }) => rest);
}

export interface RunRateRow {
  difficulty: string;
  avgUserRunRate: number;
  avgAiRunRate: number;
  matches: number;
}

export function bucketRunRateRows(rows: RunRateRow[], size: DifficultyBucketSize): RunRateRow[] {
  if (size === 'individual') return rows;
  const groups = new Map<
    string,
    {
      difficulty: string;
      userRunsTotal: number;
      aiRunsTotal: number;
      matches: number;
      sortKey: number;
    }
  >();
  for (const r of rows) {
    const v = numericValue(r.difficulty);
    if (v == null) {
      const existing = groups.get(r.difficulty);
      if (existing) {
        existing.userRunsTotal += r.avgUserRunRate * r.matches;
        existing.aiRunsTotal += r.avgAiRunRate * r.matches;
        existing.matches += r.matches;
      } else {
        groups.set(r.difficulty, {
          difficulty: r.difficulty,
          userRunsTotal: r.avgUserRunRate * r.matches,
          aiRunsTotal: r.avgAiRunRate * r.matches,
          matches: r.matches,
          sortKey: Number.MAX_SAFE_INTEGER,
        });
      }
      continue;
    }
    const key = bucketKey(v, size);
    const sortKey = Math.floor(v / size) * size;
    const existing = groups.get(key);
    if (existing) {
      existing.userRunsTotal += r.avgUserRunRate * r.matches;
      existing.aiRunsTotal += r.avgAiRunRate * r.matches;
      existing.matches += r.matches;
    } else {
      groups.set(key, {
        difficulty: key,
        userRunsTotal: r.avgUserRunRate * r.matches,
        aiRunsTotal: r.avgAiRunRate * r.matches,
        matches: r.matches,
        sortKey,
      });
    }
  }
  return Array.from(groups.values())
    .map((g) => ({
      difficulty: g.difficulty,
      avgUserRunRate: g.matches > 0 ? Math.round((g.userRunsTotal / g.matches) * 100) / 100 : 0,
      avgAiRunRate: g.matches > 0 ? Math.round((g.aiRunsTotal / g.matches) * 100) / 100 : 0,
      matches: g.matches,
    }))
    .sort((a, b) => {
      const av = numericValue(a.difficulty);
      const bv = numericValue(b.difficulty);
      if (av != null && bv != null) return av - bv;
      if (av == null) return 1;
      if (bv == null) return -1;
      return 0;
    });
}

/**
 * Shared Segmented options — kept here so the control reads identically
 * on the global and per-player pages.
 */
export const DIFFICULTY_BUCKET_OPTIONS = [
  { label: 'Individual', value: 'individual' },
  { label: 'Bucket of 5', value: '5' },
  { label: 'Bucket of 10', value: '10' },
  { label: 'Bucket of 20', value: '20' },
] as const;

export const parseBucketValue = (v: string): DifficultyBucketSize =>
  v === 'individual' ? 'individual' : (parseInt(v, 10) as 5 | 10 | 20);
